import { TeamModel } from '../types/auth';
import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh } from './tokenRefresh';
import { debugError } from '../utils/debug';
import { ExpenseTemplateModel } from '../types/case';
import type { FeatureUsageByEntityResponse } from './adminApiService';

export interface TeamUpdateRequest {
    teamName: string;
    accentColor: string;
    logoFile?: File;
}

export interface TeamUpdateResponse {
    success: boolean;
    message: string;
    team?: TeamModel;
}

/**
 * Result of canceling the team's subscription. A paid team keeps access until `effectiveOn` (the
 * end of the paid period) and is not charged again; a free team, or one with no paid period left,
 * ends now (`endedNow`) and every session is logged out.
 */
export interface CancelSubscriptionResponse {
    success: boolean;
    message: string;
    /** ISO date the subscription ends; null when it ended now or the call failed. */
    effectiveOn: string | null;
    endedNow: boolean;
}

/** Why a team has no upcoming renewal charge. Mirrors TeamRenewalSkipReason on the server. */
export type TeamRenewalSkipReason = 'noBillingDate' | 'free' | 'trial' | 'canceling' | 'inactive' | 'notBilled';

/**
 * The team's next renewal, computed on the server the way process-payments charges it (price
 * from the pricing module, less team credit). The web never prices it.
 */
export interface TeamRenewalSummary {
    nextBillingDate: string | null;
    renews: boolean;
    skipReason: TeamRenewalSkipReason | null;
    renewalAmount: number | null;
    creditBalance: number;
    creditApplied: number;
    amountDue: number | null;
    cancelRequestedAt: string | null;
    cancelEffectiveOn: string | null;
}

/**
 * Service for team-related API operations
 */
export class TeamApiService extends BaseApiService {
    constructor() {
        super(sharedTokenRefresh);
    }

    /**
     * Gets the full URL for the team logo
     */
    getLogoUrl(): string {
        return this.buildUrl('/v1/team/get-logo');
    }

    /**
     * Get team information
     * @returns Promise containing the team data
     */
    async getTeam(): Promise<{ success: boolean; team?: TeamModel; message?: string }> {
        try {
            const response = await this.postJson('/v1/team/get', {});
            const result = await this.handleResponse<{ success: boolean; team?: TeamModel; message?: string }>(response);
            return result;
        } catch (error) {
            debugError('Error getting team:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while retrieving team information'
            };
        }
    }

    /**
     * The signed-in user's team's next renewal, as process-payments will charge it.
     */
    async getRenewalSummary(): Promise<{ success: boolean; summary?: TeamRenewalSummary; message?: string }> {
        try {
            const response = await this.postJson('/v1/team/renewal-summary/get', {});
            return await this.handleResponse<{ success: boolean; summary?: TeamRenewalSummary; message?: string }>(response);
        } catch (error) {
            debugError('Error getting renewal summary:', error);
            return { success: false, message: error instanceof Error ? error.message : 'Could not load the renewal summary' };
        }
    }

    /**
     * Update team information
     * @param teamData - The team data to update
     * @returns Promise containing the update response
     */
    async updateTeam(teamData: TeamUpdateRequest): Promise<TeamUpdateResponse> {
        try {
            const formData = new FormData();
            formData.append('teamName', teamData.teamName);
            formData.append('accentColor', teamData.accentColor);

            if (teamData.logoFile) {
                formData.append('logoFile', teamData.logoFile);
            }

            const response = await this.postFormData('/v1/team/update', formData);
            const result = await this.handleResponse<TeamUpdateResponse>(response);
            return result;
        } catch (error) {
            debugError('Error updating team:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while updating team'
            };
        }
    }

    /**
     * Cancel team subscription
     * @returns Promise containing the cancellation response
     */
    /**
     * ProTrial: how many of this team's cases would become read-only if it dropped off Pro.
     * Shown in the downgrade confirmation so the choice is informed.
     */
    async getProDataCaseCount(): Promise<{ success: boolean; totalCases: number; casesWithProData: number; unknownCases: number }> {
        const response = await this.postJson('/v1/team/trial/pro-data-case-count', {});
        return await this.handleResponse<{ success: boolean; totalCases: number; casesWithProData: number; unknownCases: number }>(response);
    }

    /**
     * ProTrial: ends a lapsed Pro trial by moving the whole team to Standard. Callable by any
     * team member. Force-logs-out everyone, so the caller should log out immediately after.
     */
    async downgradeAfterTrial(): Promise<{ success: boolean; message: string }> {
        const response = await this.postJson('/v1/team/trial/downgrade', {});
        return await this.handleResponse<{ success: boolean; message: string }>(response);
    }

    /**
     * ProTrialSelfSignup: whether this team may start its own two-week trial, and why not when it
     * cannot. Used to decide whether the upgrade modal offers the trial at all.
     */
    async getSelfServiceTrialEligibility(): Promise<{ success: boolean; eligible: boolean; reason: string | null; weeks: number }> {
        const response = await this.postJson('/v1/team/trial/self-start-eligibility', {});
        return await this.handleResponse<{ success: boolean; eligible: boolean; reason: string | null; weeks: number }>(response);
    }

    /**
     * ProTrialSelfSignup: starts this team's own two-week Pro trial. Force-logs-out the whole
     * team, so the caller should log out immediately after, the same as downgradeAfterTrial.
     */
    async startSelfServiceTrial(): Promise<{ success: boolean; message: string; trialEndsAt?: string }> {
        const response = await this.postJson('/v1/team/trial/self-start', {});
        return await this.handleResponse<{ success: boolean; message: string; trialEndsAt?: string }>(response);
    }

    async cancelSubscription(): Promise<CancelSubscriptionResponse> {
        try {
            const response = await this.postJson('/v1/team/cancel-subscription', {});
            const result = await this.handleResponse<CancelSubscriptionResponse>(response);
            return result;
        } catch (error) {
            debugError('Error cancelling subscription:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while cancelling subscription',
                effectiveOn: null,
                endedNow: false,
            };
        }
    }

    /**
     * Delete team logo
     * @returns Promise containing the deletion response
     */
    async deleteLogo(): Promise<TeamUpdateResponse> {
        try {
            const response = await this.postJson('/v1/team/delete-logo', {});
            const result = await this.handleResponse<TeamUpdateResponse>(response);
            return result;
        } catch (error) {
            debugError('Error deleting logo:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while deleting the team logo'
            };
        }
    }

    /**
     * Preview PDF with team logo
     * @param pdfType - The type of PDF to preview (summary, worksheet1, financial, division, summaryandworksheet1)
     * @returns Promise containing the PDF blob
     */
    async previewPdf(pdfType: string): Promise<Blob> {
        try {
            const url = this.buildUrl('/v1/pdf/preview');
            const response = await this.makeRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pdfType }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to generate preview PDF' }));
                throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            return blob;
        } catch (error) {
            debugError('Error generating preview PDF:', error);
            throw error;
        }
    }

    /**
     * Create or update an expense template for the team
     * @param template - The expense template to create
     * @param overrideExisting - Whether to override an existing template with the same name
     * @returns Promise containing the creation response
     */
    async createExpenseTemplate(
        template: ExpenseTemplateModel,
        overrideExisting: boolean = false
    ): Promise<{ success: boolean; message: string; template?: ExpenseTemplateModel }> {
        try {
            const response = await this.postJson('/v1/team/create-expense-template', {
                template,
                overrideExisting
            });
            const result = await this.handleResponse<{ success: boolean; message: string; template?: ExpenseTemplateModel }>(response);
            return result;
        } catch (error) {
            debugError('Error creating expense template:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while creating the expense template'
            };
        }
    }

    /**
     * Get all administrative contacts for the team
     * @returns Promise containing the list of administrative contacts
     */
    async getAdministrativeContacts(): Promise<{ success: boolean; contacts?: Array<{ userId: string; emailAddress: string; firstName: string; lastName: string; fullName: string }>; message?: string }> {
        try {
            const response = await this.postJson('/v1/team/get-admin-contacts', {});
            const result = await this.handleResponse<{ success: boolean; contacts?: Array<{ userId: string; emailAddress: string; firstName: string; lastName: string; fullName: string }>; message?: string }>(response);
            return result;
        } catch (error) {
            debugError('Error getting administrative contacts:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while retrieving administrative contacts'
            };
        }
    }

    /**
     * Set or unset a user as an administrative contact
     * @param userId - The user ID to update
     * @param isAdministrativeContact - Whether to set as admin contact
     * @returns Promise containing the update response
     */
    async setAdministrativeContact(userId: string, isAdministrativeContact: boolean): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.postJson('/v1/team/set-admin-contact', {
                userId,
                isAdministrativeContact
            });
            const result = await this.handleResponse<{ success: boolean; message: string }>(response);
            return result;
        } catch (error) {
            debugError('Error setting administrative contact:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : 'An error occurred while setting administrative contact'
            };
        }
    }

    async getTeamFeatureUsage(): Promise<{ success: boolean; featureUsage?: FeatureUsageByEntityResponse; message?: string }> {
        try {
            const response = await this.makeRequest(this.buildUrl('/v1/team/feature-usage'), { method: 'GET' });
            return await this.handleResponse<{ success: boolean; featureUsage?: FeatureUsageByEntityResponse; message?: string }>(response);
        } catch (error) {
            debugError('Error getting team feature usage:', error);
            return { success: false };
        }
    }
}

// Export a singleton instance
export const teamApiService = new TeamApiService();
